import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpResponse,
  HttpHandler,
  HttpEvent,
  HttpInterceptor
} from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { delay } from 'rxjs/operators';

/**
 * Mock HTTP Interceptor for Testing
 * 
 * This interceptor mocks all API responses routed through the backend gateway
 * Enable by uncommenting in app.module.ts:
 * 
 * { provide: HTTP_INTERCEPTORS, useClass: MockHttpInterceptor, multi: true }
 */
@Injectable()
export class MockHttpInterceptor implements HttpInterceptor {
  private cars: any[] = [
    {
      carId: 1,
      brand: 'Toyota',
      model: 'Corolla',
      year: 2020,
      color: 'Silver',
      price: 25000,
      description: 'Well maintained, low mileage'
    },
    {
      carId: 2,
      brand: 'Honda',
      model: 'Civic',
      year: 2021,
      color: 'Blue',
      price: 22000,
      description: 'Excellent condition'
    },
    {
      carId: 3,
      brand: 'BMW',
      model: '3 Series',
      year: 2022,
      color: 'Black',
      price: 45000,
      description: 'Premium vehicle'
    }
  ];

  private users: any[] = [
    {
      userId: 1,
      username: 'testuser',
      email: 'test@example.com',
      password: 'testpass123',
      firstName: 'Test',
      lastName: 'User',
      phone: '+1-555-123-4567',
      role: 'user'
    },
    {
      userId: 2,
      username: 'admin',
      email: 'admin@example.com',
      password: 'admin123',
      firstName: 'Admin',
      lastName: 'User',
      phone: '+1-555-111-1111',
      role: 'admin'
    }
  ];

  private currentUser: any = null;
  private nextCarId = 4;
  private nextUserId = 3;

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const { url, method, body } = request;

    // Authentication endpoint
    if (url.includes('/authenticate') && method === 'POST') {
      return this.handleLogin(body);
    }

    // Check if authenticated (except for login)
    if (!url.includes('/authenticate') && !this.currentUser) {
      return throwError(() => ({
        status: 401,
        message: 'Unauthorized - Please login first'
      })).pipe(delay(500));
    }

    // Car endpoints
    if (url.includes('/cars')) {
      if (method === 'GET' && !url.includes('/cars/')) {
        return this.handleGetCars();
      }
      if (method === 'GET' && url.includes('/cars/')) {
        const carId = parseInt(url.split('/').pop());
        return this.handleGetCar(carId);
      }
      if (method === 'POST') {
        return this.handleAddCar(body);
      }
      if (method === 'PUT') {
        const carId = parseInt(url.split('/').pop());
        return this.handleUpdateCar(carId, body);
      }
      if (method === 'DELETE') {
        const carId = parseInt(url.split('/').pop());
        return this.handleDeleteCar(carId);
      }
    }

    // User endpoints
    if (url.includes('/users')) {
      if (method === 'GET' && !url.includes('/users/')) {
        return this.handleGetUsers();
      }
      if (method === 'GET' && url.includes('/users/')) {
        const userId = parseInt(url.split('/').pop());
        return this.handleGetUser(userId);
      }
      if (method === 'POST') {
        return this.handleAddUser(body);
      }
      if (method === 'PUT') {
        const userId = parseInt(url.split('/').pop());
        return this.handleUpdateUser(userId, body);
      }
      if (method === 'DELETE') {
        const userId = parseInt(url.split('/').pop());
        return this.handleDeleteUser(userId);
      }
    }

    // Health check
    if (url.includes('/health')) {
      return of(new HttpResponse({
        status: 200,
        body: { status: 'UP' }
      })).pipe(delay(200));
    }

    // Fallback to real request
    return next.handle(request);
  }

  private handleLogin(credentials: any): Observable<HttpEvent<any>> {
    const { username, password } = credentials;
    
    // Check credentials
    const user = this.users.find(u => u.username === username && u.password === password);
    
    if (user) {
      this.currentUser = user;
      // Mock JWT token
      const token = `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIke3VzZXIudXNlcklkfSIsInVzZXJuYW1lIjoiJHt1c2VyLnVzZXJuYW1lfSIsImlhdCI6MTY0Njk4NzIwMH0.mock_token_${Date.now()}`;
      
      return of(new HttpResponse({
        status: 200,
        body: { token, user }
      })).pipe(delay(800));
    } else {
      return throwError(() => ({
        status: 401,
        message: 'Invalid credentials'
      })).pipe(delay(800));
    }
  }

  private handleGetCars(): Observable<HttpEvent<any>> {
    return of(new HttpResponse({
      status: 200,
      body: this.cars
    })).pipe(delay(500));
  }

  private handleGetCar(carId: number): Observable<HttpEvent<any>> {
    const car = this.cars.find(c => c.carId === carId);
    if (car) {
      return of(new HttpResponse({
        status: 200,
        body: car
      })).pipe(delay(300));
    }
    return throwError(() => ({
      status: 404,
      message: 'Car not found'
    })).pipe(delay(300));
  }

  private handleAddCar(car: any): Observable<HttpEvent<any>> {
    const newCar = {
      ...car,
      carId: this.nextCarId++
    };
    this.cars.push(newCar);
    
    return of(new HttpResponse({
      status: 201,
      body: newCar
    })).pipe(delay(800));
  }

  private handleUpdateCar(carId: number, carData: any): Observable<HttpEvent<any>> {
    const car = this.cars.find(c => c.carId === carId);
    if (car) {
      Object.assign(car, carData);
      return of(new HttpResponse({
        status: 200,
        body: car
      })).pipe(delay(800));
    }
    return throwError(() => ({
      status: 404,
      message: 'Car not found'
    })).pipe(delay(800));
  }

  private handleDeleteCar(carId: number): Observable<HttpEvent<any>> {
    const index = this.cars.findIndex(c => c.carId === carId);
    if (index > -1) {
      this.cars.splice(index, 1);
      return of(new HttpResponse({
        status: 204,
        body: null
      })).pipe(delay(500));
    }
    return throwError(() => ({
      status: 404,
      message: 'Car not found'
    })).pipe(delay(500));
  }

  private handleGetUsers(): Observable<HttpEvent<any>> {
    return of(new HttpResponse({
      status: 200,
      body: this.users
    })).pipe(delay(500));
  }

  private handleGetUser(userId: number): Observable<HttpEvent<any>> {
    const user = this.users.find(u => u.userId === userId);
    if (user) {
      return of(new HttpResponse({
        status: 200,
        body: user
      })).pipe(delay(300));
    }
    return throwError(() => ({
      status: 404,
      message: 'User not found'
    })).pipe(delay(300));
  }

  private handleAddUser(userData: any): Observable<HttpEvent<any>> {
    // Check for duplicate username
    if (this.users.find(u => u.username === userData.username)) {
      return throwError(() => ({
        status: 400,
        message: 'Username already exists'
      })).pipe(delay(500));
    }

    const newUser = {
      ...userData,
      userId: this.nextUserId++
    };
    this.users.push(newUser);
    
    return of(new HttpResponse({
      status: 201,
      body: newUser
    })).pipe(delay(800));
  }

  private handleUpdateUser(userId: number, userData: any): Observable<HttpEvent<any>> {
    const user = this.users.find(u => u.userId === userId);
    if (user) {
      Object.assign(user, userData);
      return of(new HttpResponse({
        status: 200,
        body: user
      })).pipe(delay(800));
    }
    return throwError(() => ({
      status: 404,
      message: 'User not found'
    })).pipe(delay(800));
  }

  private handleDeleteUser(userId: number): Observable<HttpEvent<any>> {
    const index = this.users.findIndex(u => u.userId === userId);
    if (index > -1) {
      this.users.splice(index, 1);
      return of(new HttpResponse({
        status: 204,
        body: null
      })).pipe(delay(500));
    }
    return throwError(() => ({
      status: 404,
      message: 'User not found'
    })).pipe(delay(500));
  }
}
